import React, { useEffect, useMemo, useRef, useState } from "react";
import { getLookupByKey, isSet } from "../../../utils/commonUtils";
import FormInputs from "../../../components/UI/FormInputs";
import Buttons from "../../../components/UI/Buttons";

const defaultOperators = ["="];
const selectTypes = [
  "SelectList",
  "SelectSearchList",
  "NewSelectList",
  "NewSelectSearchList",
  "MultiSelectDropDown",
];

const normalizeFieldType = (type, ismulti = false) => {
  const inputType = isSet(type, "TextInput").toString();
  const normalizedType = inputType.toLowerCase();

  // Keep SelectSearchList/NewSelectSearchList as-is for multi mode (react-select supports isMulti).
  // Map only plain list/dropdown multi to MultiSelectDropDown.
  const isPlainSelectFamilyType =
    normalizedType === "selectlist" ||
    normalizedType === "newselectlist" ||
    normalizedType === "dropdown";

  if (ismulti === true && isPlainSelectFamilyType) {
    return "MultiSelectDropDown";
  }
  if (normalizedType === "dropdown") {
    return "SelectList";
  }
  if (normalizedType === "date") {
    return "Date";
  }
  if (normalizedType === "text") {
    return "TextInput";
  }

  const typeMap = {
    selectlist: "SelectList",
    selectsearchlist: "SelectSearchList",
    newselectlist: "NewSelectList",
    newselectsearchlist: "NewSelectSearchList",
    multiselectdropdown: "MultiSelectDropDown",
    textinput: "TextInput",
  };

  return isSet(typeMap[normalizedType], inputType);
};

const getNormalizedValues = (value) =>
  isSet(value, "")
    .toString()
    .split(",")
    .filter((item) => item !== "");

const normalizeOperators = (operators) =>
  isSet(operators, defaultOperators).map((operator) => {
    if (typeof operator === "string") {
      return { label: operator, value: operator };
    }
    const operatorValue = String(isSet(operator?.value, ""));
    return {
      label: isSet(operator?.label, operatorValue),
      value: operatorValue,
    };
  });

const getUpdatedMultiSelectValue = (currentValue, bool, selectedVal) => {
  let values = getNormalizedValues(currentValue);
  const selectedValues = Array.isArray(selectedVal) ? selectedVal : [selectedVal];

  if (bool) {
    values = values.concat(selectedValues.filter((item) => !values.includes(item)));
  } else {
    values = values.filter((item) => !selectedValues.includes(item));
  }

  return [...new Set(values)].join(",");
};

const getBetweenValues = (value) => {
  const normalizedValue = String(isSet(value, ""));
  const splitValues = normalizedValue.split(",");
  return [isSet(splitValues[0], ""), isSet(splitValues[1], "")];
};

const normalizeValidationMessage = (rawMessage) => {
  if (Array.isArray(rawMessage)) {
    return rawMessage.join(", ");
  }
  return String(isSet(rawMessage, ""));
};

const getBackendValidationMessage = (validationErrors, screenKey, index, fieldName) => {
  if (isSet(validationErrors, null) === null || typeof validationErrors !== "object") {
    return "";
  }

  const fieldAliases = {
    field_key: ["field_key", "field"],
    operator: ["operator"],
    value: ["value"],
  };
  const keysToCheck = isSet(fieldAliases[fieldName], [fieldName]);

  const directKeyCandidates = [];
  keysToCheck.forEach((key) => {
    directKeyCandidates.push(`data_filter.${screenKey}.${index}.${key}`);
    directKeyCandidates.push(`data_filter.${String(screenKey).toLowerCase()}.${index}.${key}`);
    directKeyCandidates.push(`${screenKey}.${index}.${key}`);
    directKeyCandidates.push(`${String(screenKey).toLowerCase()}.${index}.${key}`);
  });

  for (let i = 0; i < directKeyCandidates.length; i += 1) {
    const directMessage = normalizeValidationMessage(validationErrors[directKeyCandidates[i]]);
    if (directMessage !== "") {
      return directMessage;
    }
  }

  for (let i = 0; i < keysToCheck.length; i += 1) {
    const nestedMessage = normalizeValidationMessage(
      isSet(validationErrors?.data_filter?.[screenKey]?.[index]?.[keysToCheck[i]], "")
    );
    if (nestedMessage !== "") {
      return nestedMessage;
    }
  }

  return "";
};

const isConditionFieldResolved = (condition, fieldName) => {
  if (fieldName === "field_key") {
    return String(isSet(condition?.field_key, "")).trim() !== "";
  }
  if (fieldName === "operator") {
    return String(isSet(condition?.operator, "")).trim() !== "";
  }
  if (fieldName === "value") {
    const operatorValue = String(isSet(condition?.operator, "")).toLowerCase();
    if (operatorValue === "between") {
      const [fromValue, toValue] = getBetweenValues(condition?.value);
      return (
        String(isSet(fromValue, "")).trim() !== "" &&
        String(isSet(toValue, "")).trim() !== ""
      );
    }
    return String(isSet(condition?.value, "")).trim() !== "";
  }
  return false;
};

const getValidationErrorTargets = (validationErrors) => {
  const targets = [];
  if (isSet(validationErrors, null) === null || typeof validationErrors !== "object") {
    return targets;
  }

  Object.keys(validationErrors).forEach((key) => {
    const match = String(key).match(/^data_filter\.([^.]+)\.(\d+)\./);
    if (!match) {
      return;
    }
    targets.push({
      screenKey: isSet(match[1], ""),
      index: Number(match[2]),
    });
  });

  const nestedDataFilter = isSet(validationErrors?.data_filter, null);
  if (nestedDataFilter && typeof nestedDataFilter === "object") {
    Object.keys(nestedDataFilter).forEach((screenKey) => {
      const rowObj = nestedDataFilter[screenKey];
      if (!rowObj || typeof rowObj !== "object") {
        return;
      }
      Object.keys(rowObj).forEach((idx) => {
        if (`${Number(idx)}` === idx || /^\d+$/.test(String(idx))) {
          targets.push({
            screenKey: isSet(screenKey, ""),
            index: Number(idx),
          });
        }
      });
    });
  }

  const unique = new Map();
  targets.forEach((target) => {
    const key = `${target.screenKey}-${target.index}`;
    if (!unique.has(key) && target.screenKey !== "" && Number.isFinite(target.index)) {
      unique.set(key, target);
    }
  });
  return Array.from(unique.values());
};

const normalizeSchema = (data) => {
  const grouped = {};
  isSet(data, []).forEach((item) => {
    const screenKey = isSet(item.screen_key, "");
    if (screenKey === "") {
      return;
    }
    if (!grouped[screenKey]) {
      grouped[screenKey] = [];
    }

    isSet(item.filters, []).forEach((filterItem) => {
      const fieldKey = isSet(filterItem.field_key, "");
      if (fieldKey === "") {
        return;
      }
      const isExisting = grouped[screenKey].some((f) => f.field_key === fieldKey);
      if (!isExisting) {
        grouped[screenKey].push(filterItem);
      }
    });
  });
  return grouped;
};

const getInitialCondition = (filters) => {
  return {
    field_key: "",
    operator: "",
    value: "",
    type: "TextInput",
    conditional: "",
  };
};

const normalizeDataFilterValue = (dataFilterValue) => {
  if (isSet(dataFilterValue, null) === null || typeof dataFilterValue !== "object") {
    return {};
  }
  return dataFilterValue;
};

const normalizeOptionList = (list) =>
  isSet(list, []).map((item) => {
    if (typeof item === "string" || typeof item === "number") {
      return { label: String(item), value: String(item) };
    }
    const rawValue = isSet(item?.value, isSet(item?.id, ""));
    return {
      label: isSet(item?.label, isSet(item?.name, String(isSet(item?.value, "")))),
      value: String(rawValue),
    };
  });

const mergeUniqueOptions = (optionGroups) => {
  const optionMap = new Map();
  isSet(optionGroups, []).forEach((options) => {
    isSet(options, []).forEach((option) => {
      const normalizedOption = {
        label: isSet(option?.label, ""),
        value: isSet(option?.value, ""),
      };
      const key = String(normalizedOption.value);
      if (key !== "" && !optionMap.has(key)) {
        optionMap.set(key, normalizedOption);
      }
    });
  });
  return Array.from(optionMap.values());
};

const getConditionalSignature = (conditionalOptionValue) =>
  getNormalizedValues(conditionalOptionValue).sort().join(",");

const FilterConditions = ({ data, value, onChange, validationErrors, setValidationMessage }) => {
  const groupedFilters = useMemo(() => normalizeSchema(data), [data]);
  const screenKeys = useMemo(() => Object.keys(groupedFilters), [groupedFilters]);
  const resolvedDataFilter = useMemo(() => normalizeDataFilterValue(value), [value]);
  const dataFilterRef = useRef(resolvedDataFilter);
  const isHydratingFromParentRef = useRef(false);
  const handledValidationTargetsSignatureRef = useRef("");
  const conditionalLoadSignatureByRowRef = useRef({});
  const conditionalLoadingSignatureByRowRef = useRef({});
  const loadingLookupKeysRef = useRef(new Set());
  const lookupOptionsByKeyRef = useRef({});
  const [selectedScreen, setSelectedScreen] = useState(isSet(screenKeys[0], ""));
  const [conditionsByScreen, setConditionsByScreen] = useState({});
  const [lookupOptionsByKey, setLookupOptionsByKey] = useState({});
  const [conditionalSelectionByRow, setConditionalSelectionByRow] = useState({});
  const [conditionalValueOptionsByRow, setConditionalValueOptionsByRow] = useState({});
  const validationErrorTargets = useMemo(
    () => getValidationErrorTargets(validationErrors),
    [validationErrors]
  );
  const validationErrorTargetsSignature = useMemo(
    () =>
      isSet(validationErrorTargets, [])
        .map((item) => `${item.screenKey}-${item.index}`)
        .sort()
        .join("|"),
    [validationErrorTargets]
  );
  const availableScreenKeys = useMemo(
    () => screenKeys.filter((key) => !Object.prototype.hasOwnProperty.call(conditionsByScreen, key)),
    [screenKeys, conditionsByScreen]
  );

  useEffect(() => {
    dataFilterRef.current = resolvedDataFilter;
  }, [resolvedDataFilter]);

  useEffect(() => {
    lookupOptionsByKeyRef.current = lookupOptionsByKey;
  }, [lookupOptionsByKey]);

  const ensureLookupOptionsLoaded = async (lookupKeys = []) => {
    const keysToLoad = isSet(lookupKeys, []).filter((lookupKey) => {
      const normalizedKey = String(isSet(lookupKey, ""));
      if (normalizedKey === "") {
        return false;
      }
      if (Object.prototype.hasOwnProperty.call(lookupOptionsByKeyRef.current, normalizedKey)) {
        return false;
      }
      if (loadingLookupKeysRef.current.has(normalizedKey)) {
        return false;
      }
      loadingLookupKeysRef.current.add(normalizedKey);
      return true;
    });

    if (keysToLoad.length === 0) {
      return;
    }

    try {
      const responses = await Promise.all(
        keysToLoad.map(async (lookupKey) => ({
          lookupKey,
          options: await getLookupByKey(lookupKey, false),
        }))
      );
      setLookupOptionsByKey((prev) => {
        const next = { ...prev };
        responses.forEach(({ lookupKey, options }) => {
          next[lookupKey] = isSet(options, []);
        });
        return next;
      });
    } finally {
      keysToLoad.forEach((lookupKey) => {
        loadingLookupKeysRef.current.delete(lookupKey);
      });
    }
  };

  useEffect(() => {
    const normalizedByScreen = {};
    Object.entries(resolvedDataFilter).forEach(([screenKey, conditionList]) => {
      const normalizedConditions = isSet(conditionList, []);
      normalizedByScreen[screenKey] =
        normalizedConditions.length > 0
          ? normalizedConditions
          : [getInitialCondition(isSet(groupedFilters[screenKey], []))];
    });
    setConditionsByScreen((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(normalizedByScreen)) {
        return prev;
      }
      isHydratingFromParentRef.current = true;
      return normalizedByScreen;
    });
  }, [resolvedDataFilter, groupedFilters]);

  useEffect(() => {
    const selectedLookupKeys = new Set();
    Object.entries(conditionsByScreen).forEach(([screenKey, screenConditions]) => {
      const screenFilters = isSet(groupedFilters[screenKey], []);
      isSet(screenConditions, []).forEach((condition) => {
        const selectedField = screenFilters.find(
          (filterItem) => isSet(filterItem.field_key, "") === isSet(condition.field_key, "")
        );
        const lookupKey = isSet(selectedField?.lookup_key, "");
        if (isSet(selectedField?.islookup, false) === true && lookupKey !== "") {
          selectedLookupKeys.add(lookupKey);
        }
      });
    });

    if (selectedLookupKeys.size === 0) {
      return undefined;
    }

    let isMounted = true;
    const loadLookupOptions = async () => {
      await ensureLookupOptionsLoaded(Array.from(selectedLookupKeys));
      if (!isMounted) {
        return;
      }
    };

    loadLookupOptions();

    return () => {
      isMounted = false;
    };
  }, [conditionsByScreen, groupedFilters]);

  useEffect(() => {
    if (isSet(selectedScreen, "") === "" || availableScreenKeys.includes(selectedScreen)) {
      return;
    }
    // setSelectedScreen(isSet(availableScreenKeys[0], ""));
  }, [availableScreenKeys, selectedScreen]);

  const onScreenChange = (selected) => {
    setSelectedScreen(selected);
  };

  const onFieldChange = (screenKey, index, fieldKey) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, fieldName: "field_key" });
      setValidationMessage({ screenKey, index, fieldName: "operator" });
      setValidationMessage({ screenKey, index, fieldName: "value" });
      setValidationMessage({ screenKey, index, fieldName: "conditional" });
    }
    const screenFilters = isSet(groupedFilters[screenKey], []);
    const selectedField = screenFilters.find((f) => f.field_key === fieldKey);
    const operators = selectedField ? normalizeOperators(selectedField?.operators) : [];

    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              field_key: fieldKey,
              operator: isSet(operators[0]?.value, ""),
              value: "",
              type: isSet(selectedField?.type, "TextInput"),
              conditional: "",
            }
          : item
      ),
    }));
    if (isSet(selectedField?.islookup, false) === true && isSet(selectedField?.lookup_key, "") !== "") {
      ensureLookupOptionsLoaded([selectedField.lookup_key]);
    }
    const rowKey = `${screenKey}-${index}`;
    setConditionalSelectionByRow((prev) => ({
      ...prev,
      [rowKey]: "",
    }));
    setConditionalValueOptionsByRow((prev) => {
      const updated = { ...prev };
      delete updated[rowKey];
      return updated;
    });
    delete conditionalLoadSignatureByRowRef.current[rowKey];
    delete conditionalLoadingSignatureByRowRef.current[rowKey];
  };

  const onOperatorChange = (screenKey, index, operator) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, fieldName: "operator" });
      setValidationMessage({ screenKey, index, fieldName: "value" });
    }
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, operator, value: "" } : item
      ),
    }));
  };

  const onValueChange = (screenKey, index, value) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, fieldName: "value" });
    }
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, value } : item
      ),
    }));
  };

  const loadConditionalValueOptionsForRow = async (
    screenKey,
    index,
    conditionalOptionValue,
    rowConditionOverride = null
  ) => {
    const selectedConditionalValues = getNormalizedValues(conditionalOptionValue);
    const rowKey = `${screenKey}-${index}`;
    const conditionalSignature = getConditionalSignature(conditionalOptionValue);
    if (selectedConditionalValues.length === 0) {
      delete conditionalLoadSignatureByRowRef.current[rowKey];
      delete conditionalLoadingSignatureByRowRef.current[rowKey];
      setConditionalValueOptionsByRow((prev) => {
        const updated = { ...prev };
        delete updated[rowKey];
        return updated;
      });
      return;
    }
    if (
      conditionalLoadSignatureByRowRef.current[rowKey] === conditionalSignature &&
      (Array.isArray(conditionalValueOptionsByRow[rowKey]) ||
        conditionalLoadingSignatureByRowRef.current[rowKey] === conditionalSignature)
    ) {
      return;
    }
    conditionalLoadSignatureByRowRef.current[rowKey] = conditionalSignature;
    conditionalLoadingSignatureByRowRef.current[rowKey] = conditionalSignature;

    try {
      const rowCondition = isSet(
        rowConditionOverride,
        isSet(conditionsByScreen[screenKey], [])[index]
      );
      const selectedField = isSet(groupedFilters[screenKey], []).find(
        (field) => isSet(field.field_key, "") === isSet(rowCondition?.field_key, "")
      );
      let options = [];

      if (typeof selectedField?.conditional_callback === "function") {
        const callbackResults = await Promise.all(
          selectedConditionalValues.map((currentConditionalValue) =>
            selectedField.conditional_callback({
              conditionalOptionValue: currentConditionalValue,
              selectedConditionalValues,
              screenKey,
              index,
              condition: rowCondition,
              screenFilters: isSet(groupedFilters[screenKey], []),
              allConditionsByScreen: conditionsByScreen,
            })
          )
        );
        options = mergeUniqueOptions(callbackResults.map((result) => normalizeOptionList(result)));
      } else {
        const mappedResults = selectedConditionalValues.map(
          (currentConditionalValue) =>
            isSet(selectedField?.conditional_values_map, {})[currentConditionalValue]
        );
        options = mergeUniqueOptions(mappedResults.map((result) => normalizeOptionList(result)));
      }

      setConditionalValueOptionsByRow((prev) => ({
        ...prev,
        [rowKey]: isSet(options, []),
      }));
      delete conditionalLoadingSignatureByRowRef.current[rowKey];
    } catch (e) {
      delete conditionalLoadSignatureByRowRef.current[rowKey];
      delete conditionalLoadingSignatureByRowRef.current[rowKey];
      setConditionalValueOptionsByRow((prev) => ({
        ...prev,
        [rowKey]: [],
      }));
    }
  };

  const onMultiSelectValueChange = (screenKey, index, bool, selectedVal) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, fieldName: "value" });
    }
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              value: getUpdatedMultiSelectValue(item.value, bool, selectedVal),
            }
          : item
      ),
    }));
  };

  const addCondition = (screenKey) => {
    const screenFilters = isSet(groupedFilters[screenKey], []);
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: [...isSet(prev[screenKey], []), getInitialCondition(screenFilters)],
    }));
  };

  const clearValidationForRow = (screenKey, index) => {
    if (typeof setValidationMessage !== "function") {
      return;
    }
    ["field_key", "operator", "value", "conditional"].forEach((fieldName) => {
      setValidationMessage({ screenKey, index, fieldName });
    });
  };

  const clearValidationForScreen = (screenKey) => {
    if (typeof setValidationMessage !== "function") {
      return;
    }
    setValidationMessage({ screenKey, action: "clearScreen" });
  };

  const removeCondition = (screenKey, index) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, action: "removeIndex" });
    }
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).filter((_, itemIndex) => itemIndex !== index),
    }));
    const rowKey = `${screenKey}-${index}`;
    setConditionalSelectionByRow((prev) => {
      const updated = { ...prev };
      delete updated[rowKey];
      return updated;
    });
    setConditionalValueOptionsByRow((prev) => {
      const updated = { ...prev };
      delete updated[rowKey];
      return updated;
    });
    delete conditionalLoadSignatureByRowRef.current[rowKey];
    delete conditionalLoadingSignatureByRowRef.current[rowKey];
  };

  const removeScreenAccordion = (screenKey) => {
    clearValidationForScreen(screenKey);
    setConditionsByScreen((prev) => {
      const updated = { ...prev };
      delete updated[screenKey];
      return updated;
    });
    setConditionalSelectionByRow((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([key, val]) => {
        if (!key.startsWith(`${screenKey}-`)) {
          updated[key] = val;
        }
      });
      return updated;
    });
    setConditionalValueOptionsByRow((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([key, val]) => {
        if (!key.startsWith(`${screenKey}-`)) {
          updated[key] = val;
        }
      });
      return updated;
    });
    Object.keys(conditionalLoadSignatureByRowRef.current).forEach((key) => {
      if (key.startsWith(`${screenKey}-`)) {
        delete conditionalLoadSignatureByRowRef.current[key];
      }
    });
    Object.keys(conditionalLoadingSignatureByRowRef.current).forEach((key) => {
      if (key.startsWith(`${screenKey}-`)) {
        delete conditionalLoadingSignatureByRowRef.current[key];
      }
    });
  };

  const addScreenAccordion = () => {
    if (isSet(selectedScreen, "") === "") {
      return;
    }
    const existing = isSet(conditionsByScreen[selectedScreen], []);
    if (existing.length > 0) {
      addCondition(selectedScreen);
      setSelectedScreen("");
      return;
    }
    const screenFilters = isSet(groupedFilters[selectedScreen], []);
    setConditionsByScreen((prev) => ({
      ...prev,
      [selectedScreen]: [getInitialCondition(screenFilters)],
    }));
    setSelectedScreen("");
  };

  const renderValueInput = (condition, screenKey, index, optionsOverride = null) => {
    const screenFilters = isSet(groupedFilters[screenKey], []);
    const selectedField = screenFilters.find((f) => f.field_key === condition.field_key);
    const fieldType = normalizeFieldType(selectedField?.type, isSet(selectedField?.ismulti, false));
    const lookupOptions = isSet(lookupOptionsByKey[isSet(selectedField?.lookup_key, "")], []);
    const defaultFieldOptions =
      isSet(selectedField?.islookup, false) === true
        ? lookupOptions
        : isSet(selectedField?.options, []);
    const fieldOptions = optionsOverride !== null ? isSet(optionsOverride, []) : defaultFieldOptions;
    const inputOptions = selectTypes.includes(fieldType)
      ? fieldType === "MultiSelectDropDown" || fieldType === "SelectSearchList" || fieldType === "NewSelectSearchList"
        ? fieldOptions
        : [{ label: "Select value", value: "" }, ...fieldOptions]
      : [];
    const placeholder =
      fieldType === "Date"
        ? "MM/DD/YYYY"
        : isSet(selectedField?.placeholder, "Enter value");
    const operatorValue = String(isSet(condition.operator, "")).toLowerCase();
    const isBetweenOperator = operatorValue === "between";

    if (isBetweenOperator && fieldType !== "MultiSelectDropDown") {
      const [fromValue, toValue] = getBetweenValues(condition.value);
      const updateBetweenValue = (nextFromValue, nextToValue) => {
        onValueChange(screenKey, index, `${isSet(nextFromValue, "")},${isSet(nextToValue, "")}`);
      };

      return (
        <div className="d-flex align-items-center">
          <div className="flex-fill">
            <FormInputs
              type={fieldType}
              className="form-control form-control-solid"
              options={inputOptions}
              placeholder={placeholder}
              value={fromValue}
              isAsync={isSet(selectedField?.isAsync, false)}
              loadCallBack={selectedField?.loadCallBack}
              isCacheNeeded={isSet(selectedField?.isCacheNeeded, false)}
              ddKey={`${condition.field_key}_${index}_from`}
              isLoaded={true}
              changeHandler={(val) => updateBetweenValue(val, toValue)}
            />
          </div>
          <span className="mx-2 opacity-75">to</span>
          <div className="flex-fill">
            <FormInputs
              type={fieldType}
              className="form-control form-control-solid"
              options={inputOptions}
              placeholder={placeholder}
              value={toValue}
              isAsync={isSet(selectedField?.isAsync, false)}
              loadCallBack={selectedField?.loadCallBack}
              isCacheNeeded={isSet(selectedField?.isCacheNeeded, false)}
              ddKey={`${condition.field_key}_${index}_to`}
              isLoaded={true}
              changeHandler={(val) => updateBetweenValue(fromValue, val)}
            />
          </div>
        </div>
      );
    }

    return (
      <FormInputs
        type={fieldType}
        className="form-control form-control-solid"
        options={inputOptions}
        placeholder={placeholder}
        value={condition.value}
        isMulti={isSet(selectedField?.ismulti, false)}
        isAsync={isSet(selectedField?.isAsync, false)}
        loadCallBack={selectedField?.loadCallBack}
        isCacheNeeded={isSet(selectedField?.isCacheNeeded, false)}
        ddKey={`${condition.field_key}_${index}`}
        isLoaded={true}
        changeHandler={
          fieldType === "MultiSelectDropDown"
            ? (bool, { selectVal }) => onMultiSelectValueChange(screenKey, index, bool, selectVal)
            : (val) => onValueChange(screenKey, index, val)
        }
      />
    );
  };

  const onConditionalOptionChange = async (screenKey, index, conditionalOptionValue) => {
    if (typeof setValidationMessage === "function") {
      setValidationMessage({ screenKey, index, fieldName: "conditional" });
      setValidationMessage({ screenKey, index, fieldName: "value" });
    }
    const rowKey = `${screenKey}-${index}`;
    setConditionsByScreen((prev) => ({
      ...prev,
      [screenKey]: isSet(prev[screenKey], []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, conditional: conditionalOptionValue } : item
      ),
    }));
    setConditionalSelectionByRow((prev) => ({
      ...prev,
      [rowKey]: conditionalOptionValue,
    }));
    onValueChange(screenKey, index, "");

    await loadConditionalValueOptionsForRow(screenKey, index, conditionalOptionValue);
  };

  useEffect(() => {
    Object.entries(conditionsByScreen).forEach(([screenKey, screenConditions]) => {
      isSet(screenConditions, []).forEach((condition, index) => {
        const selectedField = isSet(groupedFilters[screenKey], []).find(
          (field) => isSet(field.field_key, "") === isSet(condition.field_key, "")
        );
        if (isSet(selectedField?.isConditional, false) !== true) {
          return;
        }
        const savedConditional = isSet(condition.conditional, isSet(condition.condtional, ""));
        const rowKey = `${screenKey}-${index}`;
        setConditionalSelectionByRow((prev) => {
          if (isSet(prev[rowKey], "") === isSet(savedConditional, "")) {
            return prev;
          }
          return {
            ...prev,
            [rowKey]: isSet(savedConditional, ""),
          };
        });
        if (isSet(savedConditional, "") !== "") {
          const savedConditionalSignature = getConditionalSignature(savedConditional);
          const hasLoadedOptions = Array.isArray(conditionalValueOptionsByRow[rowKey]);
          if (
            conditionalLoadSignatureByRowRef.current[rowKey] === savedConditionalSignature &&
            (hasLoadedOptions ||
              conditionalLoadingSignatureByRowRef.current[rowKey] === savedConditionalSignature)
          ) {
            return;
          }
          loadConditionalValueOptionsForRow(screenKey, index, savedConditional, condition);
        } else {
          delete conditionalLoadSignatureByRowRef.current[rowKey];
          delete conditionalLoadingSignatureByRowRef.current[rowKey];
        }
      });
    });
  }, [conditionsByScreen, groupedFilters, conditionalValueOptionsByRow]);

  useEffect(() => {
    if (isHydratingFromParentRef.current === true) {
      isHydratingFromParentRef.current = false;
      return;
    }
    const normalizedByScreen = {};
    Object.entries(conditionsByScreen).forEach(([screenKey, conditionList]) => {
      normalizedByScreen[screenKey] = isSet(conditionList, []).map((condition) => ({
        field_key: isSet(condition.field_key, ""),
        operator: isSet(condition.operator, ""),
        value: isSet(condition.value, ""),
        type: isSet(condition.type, "TextInput"),
        conditional: isSet(condition.conditional, isSet(condition.condtional, "")),
      }));
    });
    if (JSON.stringify(dataFilterRef.current) === JSON.stringify(normalizedByScreen)) {
      return;
    }
    dataFilterRef.current = normalizedByScreen;
    onChange(normalizedByScreen);
  }, [conditionsByScreen, onChange]);

  useEffect(() => {
    if (validationErrorTargetsSignature === "") {
      handledValidationTargetsSignatureRef.current = "";
      return;
    }
    if (handledValidationTargetsSignatureRef.current === validationErrorTargetsSignature) {
      return;
    }
    handledValidationTargetsSignatureRef.current = validationErrorTargetsSignature;

    requestAnimationFrame(() => {
      const errorScreens = [...new Set(validationErrorTargets.map((item) => item.screenKey))];
      errorScreens.forEach((screenKey) => {
        const panelId = `data-filter-panel-${screenKey}`;
        const panel = document.getElementById(panelId);
        if (!panel) {
          return;
        }
        panel.classList.add("show");
        const accordionHeader = document.querySelector(`[data-bs-target="#${panelId}"]`);
        if (accordionHeader) {
          accordionHeader.setAttribute("aria-expanded", "true");
          accordionHeader.classList.remove("collapsed");
        }
      });

      const firstTarget = validationErrorTargets[0];
      const firstRow = document.getElementById(
        `data-filter-row-${firstTarget.screenKey}-${firstTarget.index}`
      );
      if (firstRow) {
        firstRow.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, [validationErrorTargets, validationErrorTargetsSignature]);

  if (screenKeys.length === 0) {
    return <div className="text-muted">No filter configuration available.</div>;
  }

  const selectedScreenConfig = isSet(
    isSet(data, []).find((item) => item.screen_key === selectedScreen),
    {}
  );
  const selectedScreenLabel = isSet(selectedScreenConfig.screen_label, selectedScreen);
  const panelId = `data-filter-panel-${selectedScreen}`;

  return (
    <div className="row">
      <div className="col-12 mb-3">
        <label className="text-gray-900 text-md opacity-75 mb-1 d-block">Data</label>
        <div className="d-flex align-items-center">
          <div style={{ minWidth: "300px", maxWidth: "380px" }}>
            <FormInputs
              type="SelectList"
              className="form-control form-control-solid"
              value={selectedScreen}
              options={[
                { label: "-Select-", value: "" },
                ...availableScreenKeys.map((key) => ({ label: key, value: key })),
              ]}
              changeHandler={(val) => onScreenChange(val)}
            />
          </div>
          <div className="btn-group mb-1 ml-2">
            <Buttons
              type="Single"
              className="btn btn-primary"
              label="Add"
              clickHandler={addScreenAccordion}
              disabled={isSet(selectedScreen, "") === ""}
              acl={["any"]}
            />
          </div>
        </div>
      </div>

      {Object.keys(conditionsByScreen).length > 0 ? (
        <div className="col-12" id="accordion1">
          {Object.entries(conditionsByScreen).map(([screenKey, screenConditions]) => {
            const screenFilters = isSet(groupedFilters[screenKey], []);
            const canAddMoreCondition =
              isSet(screenConditions, []).length < isSet(screenFilters, []).length;
            const screenData = isSet(isSet(data, []).find((item) => item.screen_key === screenKey), {});
            const screenLabel = isSet(screenData.screen_label, screenKey);
            const screenPanelId = `data-filter-panel-${screenKey}`;
            return (
              <div className="card m-2 shadow-none border-0" key={screenKey}>
                <div
                  className="card-header sh_accordionheader py-2 accordion-button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#${screenPanelId}`}
                  role="button"
                  aria-expanded="true"
                  aria-controls={screenPanelId}
                >
                  <div className="d-flex pr-3 justify-content-between align-items-center w-100">
                    <h6 className="m-0 text-md">{isSet(screenLabel, "")}</h6>
                  </div>
                </div>

                <div
                  className="btn-group mb-1 position-absolute"
                  style={{ right: "5%", top: "5px", zIndex: 10 }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Buttons
                    type="Single"
                    className="btn btn-datatable btn-icon btn-transparent-dark"
                    label={null}
                    title="Delete"
                    clickHandler={() => removeScreenAccordion(screenKey)}
                    acl={["any"]}
                  >
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M13.4483 17.8637H6.54831C5.67581 17.8637 4.95081 17.1904 4.88581 16.3195L4.13831 6.19702H15.8333L15.1108 16.3154C15.0483 17.1879 14.3225 17.8637 13.4483 17.8637V17.8637Z" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.99998 9.53052V14.5305" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.33331 6.19718H16.6666" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14.1666 6.1971L13.3225 3.94544C13.0783 3.2946 12.4566 2.86377 11.7616 2.86377H8.23831C7.54331 2.86377 6.92165 3.2946 6.67748 3.94544L5.83331 6.1971" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12.8583 9.53052L12.5 14.5305" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7.14167 9.53052L7.50001 14.5305" stroke="#1D2433" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Buttons>
                </div>

                <div id={screenPanelId} className="accordion-collapse collapse show" data-bs-parent="#accordion1">
                  <div className="card-body sh_datafilter_config_box p-2">
                    {isSet(screenConditions, []).map((condition, index) => {
                      const selectedField = screenFilters.find((f) => f.field_key === condition.field_key);
                      const operators = selectedField ? normalizeOperators(selectedField?.operators) : [];
                      const backendFieldError = getBackendValidationMessage(
                        validationErrors,
                        screenKey,
                        index,
                        "field_key"
                      );
                      const backendOperatorError = getBackendValidationMessage(
                        validationErrors,
                        screenKey,
                        index,
                        "operator"
                      );
                      const backendValueError = getBackendValidationMessage(
                        validationErrors,
                        screenKey,
                        index,
                        "value"
                      );
                      const fieldError = isConditionFieldResolved(condition, "field_key")
                        ? ""
                        : backendFieldError;
                      const operatorError = isConditionFieldResolved(condition, "operator")
                        ? ""
                        : backendOperatorError;
                      const valueError = isConditionFieldResolved(condition, "value")
                        ? ""
                        : backendValueError;
                      const isConditionalField = isSet(selectedField?.isConditional, false) === true;
                      const isBetweenOperator = String(isSet(condition.operator, "")).toLowerCase() === "between";
                      const operatorColumnClass = isConditionalField || isBetweenOperator ? "col-lg-3" : "col-lg-4";
                      const valueColumnClass = isBetweenOperator
                        ? "col-lg-5"
                        : isConditionalField
                          ? "col-lg-3"
                          : "col-lg-4";
                      const rowKey = `${screenKey}-${index}`;
                      const conditionalOptions = isSet(
                        selectedField?.conditional_options,
                        isSet(selectedField?.conditionalOptions, [])
                      );
                      const conditionalSelectedValue = isSet(
                        conditionalSelectionByRow[rowKey],
                        isSet(condition.conditional, isSet(condition.condtional, ""))
                      );
                      const isConditionalMulti = isSet(selectedField?.conditional_ismulti, false);
                      const conditionalValueOptions = isSet(
                        conditionalValueOptionsByRow[rowKey],
                        null
                      );

                      return (
                        <div
                          className="d-flex align-items-center mb-3"
                          key={`${screenKey}-${condition.field_key}-${index}`}
                          id={`data-filter-row-${screenKey}-${index}`}
                        >
                          <div className="form-group w-100 mb-0">
                            <div className="d-flex justify-content-center align-items-center">
                              <div className="sh_datafilter_config_card p-2 w-100">
                                <div className="row">
                                  <div className={`${isConditionalField ? "col-lg-3" : "col-lg-4"} col-md-6 col-12`}>
                                    <div className="form-group">
                                      <label className="text-sm text-truncate mb-0 opacity-75">Field Name</label>
                                      <FormInputs
                                        type="SelectList"
                                        className="form-control form-control-solid"
                                        value={condition.field_key}
                                        options={[
                                          { label: "select", value: "" },
                                          ...screenFilters
                                            .filter((filterItem) => {
                                              const selectedInOtherRow = isSet(screenConditions, []).some(
                                                (rowCondition, rowIndex) =>
                                                  rowIndex !== index &&
                                                  isSet(rowCondition.field_key, "") ===
                                                    isSet(filterItem.field_key, "")
                                              );
                                              return !selectedInOtherRow;
                                            })
                                            .map((filterItem) => ({
                                            label: isSet(filterItem.label, filterItem.field_key),
                                            value: filterItem.field_key,
                                          })),
                                        ]}
                                        changeHandler={(val) => onFieldChange(screenKey, index, val)}
                                      />
                                      {fieldError !== "" ? (
                                        <span className="text-danger text-xs">{fieldError}</span>
                                      ) : null}
                                    </div>
                                  </div>

                                  {isConditionalField ? (
                                    <div className="col-lg-3 col-md-6 col-12">
                                      <div className="form-group">
                                        <label className="text-sm text-truncate mb-0 opacity-75">Conditional</label>
                                        <FormInputs
                                          type={isConditionalMulti ? "SelectSearchList" : "SelectList"}
                                          className="form-control form-control-solid"
                                          value={conditionalSelectedValue}
                                          isMulti={isConditionalMulti}
                                          options={conditionalOptions.map((option) => ({
                                            label: isSet(option.label, option.value),
                                            value: option.value,
                                          }))}
                                          changeHandler={(val) => onConditionalOptionChange(screenKey, index, val)}
                                        />
                                      </div>
                                    </div>
                                  ) : null}

                                  <div className={`${operatorColumnClass} col-md-6 col-12`}>
                                    <div className="form-group">
                                      <label className="text-sm text-truncate mb-0 opacity-75">Expressions</label>
                                      <FormInputs
                                        type="SelectList"
                                        className="form-control form-control-solid"
                                        value={condition.operator}
                                        options={operators}
                                        changeHandler={(val) => onOperatorChange(screenKey, index, val)}
                                      />
                                      {operatorError !== "" ? (
                                        <span className="text-danger text-xs">{operatorError}</span>
                                      ) : null}
                                    </div>
                                  </div>

                                  <div className={`${valueColumnClass} col-md-6 col-12`}>
                                    <div className="form-group">
                                      <label className="text-sm text-truncate mb-0 opacity-75">Value</label>
                                      {renderValueInput(
                                        condition,
                                        screenKey,
                                        index,
                                        isConditionalField ? isSet(conditionalValueOptions, []) : conditionalValueOptions
                                      )}
                                      {valueError !== "" ? (
                                        <span className="text-danger text-xs">{valueError}</span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="actions ml-2 d-flex align-items-center">
                            {canAddMoreCondition ? (
                              <button
                                type="button"
                                className="btn btn-icon btn-transparent-dark"
                                title="Add"
                                style={{ cursor: "pointer" }}
                                onClick={() => addCondition(screenKey)}
                              >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 18C12.34 18 12.6252 17.8848 12.8556 17.6544C13.0852 17.4248 13.2 17.14 13.2 16.8V13.2H16.83C17.17 13.2 17.45 13.0848 17.67 12.8544C17.89 12.6248 18 12.34 18 12C18 11.66 17.8848 11.3748 17.6544 11.1444C17.4248 10.9148 17.14 10.8 16.8 10.8H13.2V7.17C13.2 6.83 13.0852 6.55 12.8556 6.33C12.6252 6.11 12.34 6 12 6C11.66 6 11.3752 6.1148 11.1456 6.3444C10.9152 6.5748 10.8 6.86 10.8 7.2V10.8H7.17C6.83 10.8 6.55 10.9148 6.33 11.1444C6.11 11.3748 6 11.66 6 12C6 12.34 6.1148 12.6248 6.3444 12.8544C6.5748 13.0848 6.86 13.2 7.2 13.2H10.8V16.83C10.8 17.17 10.9152 17.45 11.1456 17.67C11.3752 17.89 11.66 18 12 18ZM12 24C10.34 24 8.78 23.6848 7.32 23.0544C5.86 22.4248 4.59 21.57 3.51 20.49C2.43 19.41 1.5752 18.14 0.9456 16.68C0.3152 15.22 0 13.66 0 12C0 10.34 0.3152 8.78 0.9456 7.32C1.5752 5.86 2.43 4.59 3.51 3.51C4.59 2.43 5.86 1.5748 7.32 0.9444C8.78 0.3148 10.34 0 12 0C13.66 0 15.22 0.3148 16.68 0.9444C18.14 1.5748 19.41 2.43 20.49 3.51C21.57 4.59 22.4248 5.86 23.0544 7.32C23.6848 8.78 24 10.34 24 12C24 13.66 23.6848 15.22 23.0544 16.68C22.4248 18.14 21.57 19.41 20.49 20.49C19.41 21.57 18.14 22.4248 16.68 23.0544C15.22 23.6848 13.66 24 12 24Z" fill="#0223C3"></path>
                                </svg>
                              </button>
                            ) : null}
                            {isSet(screenConditions, []).length > 1 ? (
                              <button
                                type="button"
                                className="btn btn-icon btn-transparent-dark ml-1"
                                title="Delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => removeCondition(screenKey, index)}
                              >
                                <svg width="24" height="24" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="0.5" y="0.5" width="20" height="20" rx="9.5" fill="#0223C3" fillOpacity="0.16"></rect>
                                  <rect x="0.5" y="0.5" width="20" height="20" rx="9.5" stroke="#0223C3"></rect>
                                  <path d="M5.5 10.5H15.5" stroke="#0223C3" strokeWidth="2" strokeLinecap="round"></path>
                                </svg>
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="col-12">
          <div className="text-muted">Select a screen and click Add to create a filter accordion.</div>
        </div>
      )}
    </div>
  );
};

FilterConditions.defaultProps = {
  data: [],
  value: {},
  onChange: () => {},
  validationErrors: {},
  setValidationMessage: () => {},
};

export default FilterConditions;
